import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS, can, FEATURE_KEYS } from "../lib/constants";
import {
  LayoutDashboard, Boxes, Layers, Factory, FileText, ShoppingCart,
  Receipt, Wallet, Users2, Warehouse, Contact2, LogOut, Settings, Menu, X, ArrowRightLeft, AlertTriangle, Megaphone, Eye,
} from "lucide-react";
import { Button } from "./ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, area: "dashboard" },
  { to: "/raw-materials", label: "Raw Materials", icon: Boxes, area: "raw" },
  { to: "/products", label: "Products (BoM)", icon: Layers, area: "products" },
  { to: "/production", label: "Production", icon: Factory, area: "production" },
  { to: "/transfers", label: "Transfers", icon: ArrowRightLeft, area: "transfers" },
  { to: "/invoices", label: "Sales Invoices", icon: FileText, area: "invoices" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ShoppingCart, area: "po" },
  { to: "/expenses", label: "Expenses", icon: Receipt, area: "expenses" },
  { to: "/payments", label: "Payments", icon: Wallet, area: "payments" },
  { to: "/contacts", label: "Contacts", icon: Contact2, area: "contacts" },
  { to: "/warehouses", label: "Warehouses", icon: Warehouse, area: "warehouses" },
  { to: "/users", label: "Users", icon: Users2, area: "admin", feature: "users" },
  { to: "/settings", label: "Settings", icon: Settings, area: "admin" },
];

export default function AppLayout({ children }) {
  const { user, logout, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [announcements, setAnnouncements] = React.useState([]);
  const [dismissed, setDismissed] = React.useState({});

  React.useEffect(() => {
    api.get("/announcements").then(({ data }) => setAnnouncements(data)).catch(() => {});
  }, []);

  const exitImpersonation = async () => {
    try {
      await api.post("/auth/stop-impersonation");
      await refreshMe();
      navigate("/admin");
    } catch { /* ignore */ }
  };

  if (!user) return null;

  const feats = user.subscription?.features || {};
  const items = NAV.filter((n) => {
    const roleOk = n.area === "admin" ? user.role === "admin" : can(user.role, n.area);
    if (!roleOk) return false;
    const featKey = n.feature || (FEATURE_KEYS.includes(n.area) ? n.area : null);
    if (featKey && feats[featKey] === false) return false;
    return true;
  });

  const sub = user.subscription;
  let banner = null;
  if (sub) {
    if (sub.is_demo) banner = { tone: "info", msg: "Read-only demo account — changes are disabled." };
    else if (sub.status === "suspended") banner = { tone: "error", msg: "Your account is suspended. Please contact support to reactivate." };
    else if (sub.is_expired) banner = { tone: "error", msg: "Your subscription has expired. Renew to continue using StockLedger.", renew: true };
    else if (sub.read_only) banner = { tone: "info", msg: "Your account is in read-only mode. Please contact your administrator." };
    else if (sub.days_left != null && sub.days_left <= 7) banner = { tone: "warn", msg: `Your ${sub.plan === "trial" ? "trial" : "subscription"} ends in ${sub.days_left} day${sub.days_left === 1 ? "" : "s"}. Please renew before it expires.`, renew: true };
  }
  const bannerClass = { info: "bg-secondary text-foreground", warn: "bg-amber-400 text-black", error: "bg-destructive text-destructive-foreground" };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };
  const logoUrl = user.organization?.logo_url;

  return (
    <div className="min-h-screen flex bg-background noise-bg">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 h-screen w-64 shrink-0 border-r border-border bg-card flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center gap-2 px-5 border-b border-border">
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="w-8 h-8 object-contain" />
          ) : (
            <div className="w-8 h-8 bg-primary flex items-center justify-center">
              <Boxes className="w-5 h-5 text-primary-foreground" />
            </div>
          )}
          <span className="font-display font-black text-lg tracking-tighter truncate">{user.organization?.name || "StockLedger"}</span>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.area}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <div className="px-2 pb-2">
            <div className="text-sm font-semibold truncate">{user.name}</div>
            <div className="text-xs text-muted-foreground truncate">{ROLE_LABELS[user.role]}</div>
          </div>
          <Button
            variant="outline"
            className="w-full rounded-none justify-start gap-2"
            onClick={doLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </Button>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {user.impersonating && (
          <div className="flex items-center gap-3 px-4 lg:px-8 py-2.5 text-sm font-semibold bg-primary text-primary-foreground" data-testid="impersonation-banner">
            <Eye className="w-4 h-4 shrink-0" />
            <span className="flex-1">You are viewing <b>{user.organization?.name}</b> as an administrator (impersonation).</span>
            <button onClick={exitImpersonation} className="underline underline-offset-2 whitespace-nowrap" data-testid="exit-impersonation-btn">Exit to Platform Admin</button>
          </div>
        )}
        <header className="h-16 border-b border-border bg-card flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20">
          <button
            className="lg:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-3 ml-auto">
            <div className="text-right hidden sm:block">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                {user.organization?.name}
              </div>
              <div className="text-xs font-mono text-muted-foreground">
                Base: {user.organization?.base_currency || "NGN"}
              </div>
            </div>
          </div>
        </header>
        {banner && (
          <div className={`flex items-center gap-3 px-4 lg:px-8 py-2.5 text-sm font-medium ${bannerClass[banner.tone]}`} data-testid="subscription-banner">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="flex-1">{banner.msg}</span>
            {banner.renew && user.role === "admin" && (
              <Link to="/settings" className="underline underline-offset-2 font-semibold whitespace-nowrap" data-testid="banner-renew-link">Renew now</Link>
            )}
          </div>
        )}
        {announcements.filter((a) => !dismissed[a.id]).map((a) => (
          <div key={a.id} className={`flex items-center gap-3 px-4 lg:px-8 py-2.5 text-sm font-medium ${bannerClass[a.tone] || bannerClass.info}`} data-testid="announcement-banner">
            <Megaphone className="w-4 h-4 shrink-0" />
            <span className="flex-1">{a.message}</span>
            <button onClick={() => setDismissed((d) => ({ ...d, [a.id]: true }))} className="opacity-70 hover:opacity-100" data-testid="announcement-dismiss-btn"><X className="w-4 h-4" /></button>
          </div>
        ))}
        <main className="flex-1 p-4 lg:p-8 relative z-10">{children}</main>
      </div>
    </div>
  );
}
